import axios from "axios";

const API = process.env.REACT_APP_API_URL;
const photosUrl = `${API}/api/photos`;
const galleriesUrl = `${API}/api/galleries`;

// Lit le token à CHAQUE appel (sinon risque de token obsolète)
const authHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/* ================
   GET
   ================ */

// Récupérer toutes les photos
export const getAllPhotos = async () => {
  try {
    const { data } = await axios.get(`${photosUrl}/getAllPhotos`, {
      headers: authHeaders(),
    });
    return data;
  } catch (error) {
    console.error("Erreur lors du chargement des photos:", error);
    throw error;
  }
};

// Récupérer une photo par son id
export const getPhotoById = async (photoId) => {
  try {
    const { data } = await axios.get(`${photosUrl}/getPhotoById/${photoId}`, {
      headers: authHeaders(),
    });
    return data;
  } catch (error) {
    console.error("Erreur lors du chargement de la photo:", error);
    throw error;
  }
};

// Récupérer les photos d'une gallerie par son id (trié côté back par position ASC)
export const getPhotosByGalleryId = async (galleryId) => {
  try {
    const { data } = await axios.get(
      `${photosUrl}/getPhotosByGallerieId/${galleryId}`,
      { headers: authHeaders() }
    );
    return data;
  } catch (error) {
    console.error("Erreur lors du chargement des photos:", error);
    throw error;
  }
};

/* ================
   POST
   ================ */

// Ajouter une nouvelle photo (métadonnées JSON)
// payload attendu : { gallerie_id, photo_url?, description?, is_active?, is_cover? }
export const addPhoto = async (payload) => {
  try {
    const { data } = await axios.post(`${photosUrl}/addPhoto`, payload, {
      headers: { ...authHeaders(), "Content-Type": "application/json" },
    });
    return data;
  } catch (error) {
    console.error("Erreur lors de l'ajout de la photo:", error);
    throw error;
  }
};

// Créer + uploader le fichier en une fois (route canonique)
// file: File/Blob | fields: { description?, is_active?, is_cover? }
export const addPhotoWithUpload = async (gallerieId, file, fields = {}) => {
  try {
    const formData = new FormData();
    formData.append("image", file);
    Object.entries(fields).forEach(([k, v]) => {
      if (v !== undefined && v !== null) formData.append(k, String(v));
    });

    const { data } = await axios.post(
      `${galleriesUrl}/${gallerieId}/photos`,
      formData,
      { headers: authHeaders() } // ne pas forcer Content-Type, laisser le browser
    );
    return data;
  } catch (error) {
    console.error("Erreur lors de l'upload/creation de la photo:", error);
    throw error;
  }
};

/* ================
   PUT / PATCH
   ================ */

// Mettre à jour les métadonnées d'une photo (PAS la position, utiliser move)
export const updatePhoto = async (photoId, payload) => {
  try {
    const { data } = await axios.put(
      `${photosUrl}/editPhoto/${photoId}`,
      payload,
      { headers: authHeaders() }
    );
    return data;
  } catch (error) {
    console.error("Erreur lors de la mise à jour de la photo:", error);
    throw error;
  }
};

// Mettre à jour UNIQUEMENT le fichier image
// Compatibilité ascendante :
//   - anciens appels: updatePhotoImage(photoId, file)
//   - nouveaux appels: updatePhotoImage(photoId, file, { gallerieId })
export const updatePhotoImage = async (photoId, file, options) => {
  try {
    let gallerieId = null;
    if (options && typeof options === "object" && "gallerieId" in options) {
      gallerieId = options.gallerieId;
    }
    const formData = new FormData();
    formData.append("image", file);

    const url = gallerieId
      ? `${galleriesUrl}/${gallerieId}/photos/${photoId}/photo`
      : `${photosUrl}/${photoId}/photo`; // fallback

    const { data } = await axios.patch(url, formData, {
      headers: authHeaders(),
    });
    return data;
  } catch (error) {
    console.error("Erreur lors de la mise à jour de l'image:", error);
    throw error;
  }
};

// Basculer la couverture (unique par album)
// togglePhotoCover(photoId)  -> fallback
// togglePhotoCover(photoId, { gallerieId }) -> canonique
export const togglePhotoCover = async (photoId, options) => {
  try {
    const gallerieId =
      options && typeof options === "object" ? options.gallerieId : null;

    const url = gallerieId
      ? `${galleriesUrl}/${gallerieId}/photos/${photoId}/toggle-cover`
      : `${photosUrl}/${photoId}/toggle-cover`;

    const { data } = await axios.patch(url, null, { headers: authHeaders() });
    return data;
  } catch (error) {
    console.error("Erreur lors du toggle de couverture:", error);
    throw error;
  }
};

// Déplacer une photo dans l'album (réordonnancement)
// movePhoto(photoId, toPosition)  -> fallback
// movePhoto(photoId, toPosition, { gallerieId }) -> canonique
export const movePhoto = async (photoId, toPosition, options) => {
  try {
    const gallerieId =
      options && typeof options === "object" ? options.gallerieId : null;

    const url = gallerieId
      ? `${galleriesUrl}/${gallerieId}/photos/${photoId}/move`
      : `${photosUrl}/${photoId}/move`;

    const { data } = await axios.patch(
      url,
      { toPosition },
      { headers: { ...authHeaders(), "Content-Type": "application/json" } }
    );
    return data;
  } catch (error) {
    console.error("Erreur lors du déplacement de la photo:", error);
    throw error;
  }
};

/* ================
   DELETE
   ================ */

export const deletePhoto = async (photoId) => {
  try {
    const { data } = await axios.delete(
      `${photosUrl}/deletePhoto/${photoId}`,
      { headers: authHeaders() }
    );
    return data;
  } catch (error) {
    console.error("Erreur lors de la suppression de la photo:", error);
    throw error;
  }
};
